import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { PaymentStatus } from "@bca-confirmation/models/_enums";
import { ITransaction } from "@bca-confirmation/models/transaction";
import * as confirmationActions from "@bca-confirmation/store/confirmation.actions";
import { ApiService } from "@bca-core/services/api.service";
import { AvoTrackingService } from "@bca-core/services/avo-tracking.service";
import * as receiptActions from "@bca-receipt/store/receipt.actions";
import { IActionCode } from "@bca-shared/models/action-code";
import { IProduct } from "@bca-shared/models/product";
import { Currency, NsShop, PaymentType } from "@bca-shared/utils/avo/Avo";
import { AvoDataTransformService } from "@bca-shared/utils/avo/avo-data-transform.service";
import { SalesChanelType } from "@bca-shared/utils/avo/avo-event-details.models";

import { AvoService } from "@bca-shared/utils/avo/avo.service";
import * as appActions from "@bca-store/app.actions";
import { IAction } from "@bca-store/models/action";
import { createAction } from "@bca-store/models/action-creator";
import { IRootState } from "@bca-store/store.reducers";
import { selectTotalPrice } from "@bca-store/store.selectors";
import { select, Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import {
  combineLatest,
  filter,
  map,
  Observable,
  Subject,
  take,
  takeUntil,
  tap,
} from "rxjs";

@Component({
  selector: "bca-confirmation",
  templateUrl: "confirmation.component.html",
  styleUrls: ["confirmation.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmationComponent implements OnInit, OnDestroy {
  representativeEmail$: Observable<string>;
  statusPaymentLoading$: Observable<boolean>;
  hasPaymentFailed$: Observable<boolean>;

  PaymentStatus = PaymentStatus;
  private readonly _destroy$ = new Subject<void>();

  transaction$: Observable<ITransaction>;
  receipt$: Observable<any>;
  totalPrice$: Observable<number>;
  products: IProduct[] = [];

  transactionId: string | undefined;
  actionCode: IActionCode | undefined;

  constructor(
    private store$: Store<IRootState>,
    private _avoService: AvoService,
    private _avoTrackingService: AvoTrackingService,
    private _store$: Store<IRootState>,
    private _translate: TranslateService,
    private _avoDataTransformService: AvoDataTransformService,
    private _apiService: ApiService,
  ) {
    this.representativeEmail$ = this.store$.pipe(
      select(({ receipt }) => receipt.company.representative.email),
    );

    this.hasPaymentFailed$ = this.store$.pipe(
      select(({ confirmation }) => confirmation.paymentFailed),
    );

    this.initializeActions();

    this.receipt$ = this._store$.pipe(
      select(({ receipt }) => receipt),
      tap((receipt) => {
        this.actionCode = receipt.actionCode;
        this.products = receipt.products;
      }),
    );

    this.totalPrice$ = this._store$.pipe(select(selectTotalPrice));

    this.statusPaymentLoading$ = this._store$.pipe(
      select(({ confirmation }) => confirmation.statusPaymentLoading),
    );

    this.transaction$ = this._store$.pipe(
      select(({ confirmation }) => confirmation.transaction),
      tap((transaction) => {
        this.transactionId = transaction?.id;
      }),
    );
  }

  ngOnInit(): void {
    this.initializeActions();

    this.transaction$
      .pipe(
        filter((t) => !!t?.id),
        take(1),
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this.triggerAvoPurchaseEvent();
      });
  }

  retryPayment(): void {
    this.store$.dispatch(
      this.createAction(
        new receiptActions.SetCompletedStatus({ completed: false }),
      ),
    );
    this.store$.dispatch(
      this.createAction(new confirmationActions.SetTransaction(null)),
    );
    this.store$.dispatch(this.createAction(new appActions.PreviousRoute()));
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  resetState(): void {
    this.triggerAvoButtonClick();
    this.store$.dispatch(this.createAction(new appActions.ResetState()));
  }

  private initializeActions(): void {
    this.store$.dispatch(
      this.createAction(new confirmationActions.StatusPayment()),
    );
    this.store$.dispatch(
      this.createAction(
        new confirmationActions.RedirectToIssuerLoading({ loading: false }),
      ),
    );
  }

  private triggerAvoPurchaseEvent(): void {
    const combined$ = combineLatest([
      this.transaction$.pipe(take(1)),
      this.receipt$.pipe(take(1)),
      this.totalPrice$.pipe(take(1)),
      this.hasPaymentFailed$.pipe(take(1)),
    ]);

    combined$
      .pipe(
        map(([transaction, receipt, totalPrice, hasPaymentFailed]) => ({
          transactionId: transaction?.id,
          products: receipt?.products || [],
          totalPrice,
          actionCode: receipt?.actionCode?.code || null,
          hasPaymentFailed,
        })),
        filter(({ hasPaymentFailed }) => !hasPaymentFailed),
        tap(({ transactionId, products, totalPrice, actionCode }) => {
          this._avoService.purchase({
            currency: Currency.EUR,
            transactionId,
            coupon: actionCode,
            tax: null,
            value: totalPrice / 100,
            items: this._avoDataTransformService.transformItems(products),
            nsShop: NsShop.NS_BUSINESSCARD,
            paymentType: [PaymentType.IDEAL],
            ticketClass: undefined,
            salesChannel: SalesChanelType.BCA,
            existingProduct: products.map((product) => ({
              itemId: product.identifier,
              itemName: product.identifier,
              itemBrand: "BCA",
              itemCategory: "Abonnementen",
              itemCategory2: "Abonnementen",
              itemVariant: "BCA",
              price: product.price,
              quantity: 1,
            })),
          });
        }),
        takeUntil(this._destroy$),
      )
      .subscribe();
  }

  private triggerAvoButtonClick(): void {
    const props = {
      uniqueName: "confirmation_of_order",
      description: "thanksPage",
      clickDestination: "https://www.ns.nl/zakelijk",
      clickText: "Naar ns.nl/zakelijk",
      data: undefined,
    };

    const trackingData = this._avoTrackingService.trackButtonClick(props);

    this._avoService.buttonClick(trackingData);
  }

  private createAction(action: IAction): IAction {
    return createAction(action, ConfirmationComponent);
  }
}
